import { useRef, useState, useCallback, useEffect } from "react";
import { useGesture } from "@use-gesture/react";
import { motion } from "framer-motion";
import { LocationPin } from "./LocationPin.js";
import type { Location } from "../data/types.js";

const MIN_SCALE = 0.5;
const MAX_SCALE = 4;

const clamp = (n: number, lo: number, hi: number) =>
  Math.max(lo, Math.min(hi, n));

interface Props {
  mapImage: string;
  activeLocation: Location | null;
  onPinClick: (loc: Location) => void;
  locations: Location[];
  dimmedIds: Set<string>;
}

export function ImageMap({
  mapImage,
  activeLocation,
  onPinClick,
  locations,
  dimmedIds,
}: Props) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);
  const [pos, setPos] = useState({ x: 0, y: 0 });
  const [imgLoaded, setImgLoaded] = useState(false);
  const [coverScale, setCoverScale] = useState(1);

  // Reset transforms whenever the map image changes
  const prevMap = useRef(mapImage);
  useEffect(() => {
    if (prevMap.current !== mapImage) {
      prevMap.current = mapImage;
      setScale(1);
      setPos({ x: 0, y: 0 });
      setImgLoaded(false);
      setCoverScale(1);
    }
  }, [mapImage]);

  useGesture(
    {
      onDrag: ({ offset: [x, y] }) => {
        setPos({ x, y });
      },
      onPinch: ({ offset: [s] }) => {
        setScale(clamp(s, MIN_SCALE, MAX_SCALE));
      },
    },
    {
      target: containerRef,
      drag: {
        from: () => [pos.x, pos.y],
        bounds: () => {
          const el = containerRef.current;
          if (!el) return {};
          const w = el.offsetWidth;
          const h = el.offsetHeight;
          const total = coverScale * scale;
          const bx = Math.max(0, (w * (total - 1)) / 2);
          const by = Math.max(0, (h * (total - 1)) / 2);
          return { left: -bx, right: bx, top: -by, bottom: by };
        },
        rubberband: 0.15,
      },
      pinch: { scaleBounds: { min: MIN_SCALE, max: MAX_SCALE }, rubberband: true },
    }
  );

  const handleDoubleClick = useCallback(() => {
    if (scale > 1.1) {
      setScale(1);
      setPos({ x: 0, y: 0 });
    } else {
      setScale(2);
    }
  }, [scale]);

  const handleImageLoad = useCallback(
    (e: React.SyntheticEvent<HTMLImageElement>) => {
      setImgLoaded(true);
      const img = e.currentTarget;
      const winW = window.innerWidth;
      const winH = window.innerHeight;
      const cw = img.clientWidth;
      const ch = img.clientHeight;
      // On mobile, scale up so the image covers the viewport
      if (winW < 768) {
        const c = Math.max(winW / cw, winH / ch);
        setCoverScale(c);
      }
    },
    []
  );

  return (
    <div
      ref={containerRef}
      className="absolute inset-0 overflow-hidden touch-none select-none"
      style={{ cursor: scale > 1 ? "grab" : "default" }}
      onDoubleClick={handleDoubleClick}
    >
      <motion.div
        className="w-full h-full flex items-center justify-center"
        animate={{ scale: coverScale * scale, x: pos.x, y: pos.y }}
        transition={{ type: "spring", stiffness: 300, damping: 30 }}
        style={{ transformOrigin: "center center" }}
      >
        <div className="relative max-w-full max-h-full">
          <img
            src={mapImage}
            alt="Photo spot map"
            className="block max-w-full max-h-screen"
            onLoad={handleImageLoad}
            draggable={false}
            style={{
              opacity: imgLoaded ? 1 : 0,
              transition: "opacity 0.3s ease",
            }}
          />
          {!imgLoaded && (
            <div className="absolute inset-0 flex items-center justify-center bg-surface-1">
              <div className="text-center px-8">
                <p className="text-ink-2 text-sm mb-2">Loading map...</p>
              </div>
            </div>
          )}
          {locations.map((loc) => (
            <LocationPin
              key={loc.id}
              location={loc}
              isActive={activeLocation?.id === loc.id}
              isDimmed={dimmedIds.has(loc.id)}
              onClick={() => onPinClick(loc)}
            />
          ))}
        </div>
      </motion.div>
    </div>
  );
}
